import React, { useState, useEffect } from 'react';
import { UserCircle, Info } from "lucide-react";
import { FaRegCopy, FaCheck, FaRobot } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import TypingText from '../TypingText';
import { MessageListProps } from '../../types/types';

import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";

declare global {
  interface Window {
    MathJax?: {
      typeset?: (elements?: Array<HTMLElement>) => void;
      typesetPromise?: (elements?: Array<HTMLElement>) => Promise<any>;
      startup?: {
        promise?: Promise<any>;
      };
      tex?: any;
      svg?: any;
      options?: any;
      [key: string]: any;
    }
  }
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  configData,
  activeChatId,
  isNewChat,
  typingChatId,
  hasTyped,
  loading,
  isSidebarOpen,
  onScrollBottom,
  onChunkReference,
  messagesEndRef,
  loadingIndicatorRef,
  safeAiIconSrc
}) => {
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);

  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const isLight = isLightColor(backgroundColor);

  const lighterBackground = isLight
    ? darkenColor(backgroundColor, 5)
    : lightenColor(backgroundColor, 5);

  const darkerTabBackground = backgroundColor ? (isLight
    ? darkenColor(backgroundColor, 15)
    : lightenColor(backgroundColor, 15)) : '';

  function preserveDollarAmounts(text: string): string {
    return text.replace(/\$(\d+(?:\.\d+)?)/g, 'DOLLAR_PLACEHOLDER__$1__');
  }

  function restoreDollarAmounts(text: string): string {
    return text.replace(/DOLLAR_PLACEHOLDER__(\d+(?:\.\d+)?)__/g, (_, value) => `$${value}`);
  }

  function convertInlineMath(text: string): string {
    return text.replace(
      /(?<![\\$])\$(?!\d)((?:\\.|[^$\\])+?)(?<![\\])\$/g,
      (_, math) => `\\(${math.trim()}\\)`
    );
  }

  function formatText(text: string): string {
    let result = preserveDollarAmounts(text);
    result = convertInlineMath(result);
    result = result
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/###\s*(.*?)(?=\n|$)/g, "<h3>$1</h3>")
      .replace(/\n/g, "<br>")
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">$1</a>')
      .replace(/(?<!\*)\*(?!\*)(.*?)\*(?!\*)/g, "<em>$1</em>");
    
    return restoreDollarAmounts(result);
  }

  const handleTypingFinished = (isTyping: boolean) => {
    setTimeout(() => {
      if (window.MathJax && window.MathJax.typesetPromise) {
        window.MathJax.typesetPromise()
          .catch((err: any) => console.error('MathJax error in parent:', err));
      } else if (window.MathJax?.typeset) {
        window.MathJax.typeset();
      }
    }, 0);
  };

  useEffect(() => {
    if (hasTyped && typeof window !== 'undefined' && window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise()
        .catch(err => console.error("MathJax render error:", err));
    }
  }, [hasTyped]);

  return (
    <div className="space-y-2">
      {messages.map((msg, index) => {
        const isLastAssistantMessage =
          index === messages.length - 1 && msg.sender === 'assistant';

        return (
          <div
            key={index}
            className={`flex ${msg.sender === 'user' ? 'justify-end ' : 'md:pl-[2%] lg:pl-[10%] '}`}
            style={{
              color: configData.app_ui_settings.ui_fontcolor,
            }}
          >
            {msg.sender === 'user' && <UserCircle className="h-6 w-6 mr-2 mt-1" />}
            {msg.sender === 'assistant' && (
              <div className="flex items-start">
                {safeAiIconSrc ? (
                  <img
                    src={safeAiIconSrc}
                    alt="AI Face"
                    className="h-6 w-6 mr-2 mt-2 flex-shrink-0 object-contain"
                    style={{
                      color: configData.app_ui_settings.ui_iconcolor,
                      minWidth: '1.5rem',
                      minHeight: '1.5rem',
                    }}
                  />
                ) : (
<FaRobot
                    className="h-6 w-6 mr-2 mt-2"
                    style={{ color: configData.app_ui_settings.ui_fontcolor }}
                  />
                )}
              </div>
            )}
            <div
              className={`group p-3 flex flex-col ${
                msg.sender === 'user'
                  ? 'text-white max-w-[50%] rounded-lg self-end mr-[2%] lg:mr-[10%]'
                  : 'text-black justify-start mr-[10%] max-w-max'
              }`}
              style={{
                color: configData.app_ui_settings.ui_fontcolor,
                wordWrap: 'break-word',
                background: msg.sender === 'user' ? lighterBackground : '',
              }}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setActiveMessageId(prev => (prev === msg.message_id ? null : msg.message_id!));
                }
              }}
            >
              {isLastAssistantMessage && isNewChat && typingChatId === activeChatId && !hasTyped ? (
                <TypingText 
                  text={msg.message} 
                  speed={3} 
                  onTypingFinished={handleTypingFinished} 
                  onTypingUpdate={onScrollBottom}
                />
              ) : (
                <span style={{ display: 'inline' }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatText(msg.message)
                    }}
                  />
                </span>
              )}

              {msg.sender === 'assistant' && (
                <div className={`
                  flex justify-start
                  ${window.innerWidth <= 1024
                    ? activeMessageId === msg?.message_id
                      ? 'flex'
                      : 'hidden'
                    : 'hidden group-hover:flex'}
                `}>
                  {copiedMessage === msg.message ? (
                    <span 
                      className="flex items-center justify-center w-9 h-9 hover:rounded-lg"
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <FaCheck className="h-4 w-4 hover:rounded-lg" title="Copied" />
                    </span>
                  ) : (
                    <>
                      <span  
                        data-tooltip-id='copy_msg'
                        className="flex items-center justify-center w-9 h-9 hover:rounded-lg"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <FaRegCopy
                          className="h-4 w-4 cursor-pointer"
                          onClick={() => {
                            const plainText = msg.message.replace(/<[^>]+>/g, '');
                            navigator.clipboard.writeText(plainText);
                            setCopiedMessage(msg.message);
                            setTimeout(() => setCopiedMessage(null), 2000);
                          }}
                        />
                      </span>
                      <Tooltip 
                        id='copy_msg' 
                        place="bottom" 
                        style={{ 
                          color: configData?.app_ui_settings?.ui_fontcolor,
                          backgroundColor: lighterBackground
                        }}
                      >
                        Copy
                      </Tooltip>
                    </>
                  )}
                  {msg.isRefAvailable && (
                    <>
                      <span  
                        data-tooltip-id='reference_chunk'
                        className="flex items-center justify-center w-9 h-9 hover:rounded-lg"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <Info
                          className="h-4 w-4 inline-block ml-1"
                          style={{ color: '#000' }}
                          onClick={() => {
                            if (msg?.message_id) {
                              onChunkReference(msg.message_id);
                            }
                          }}
                        />
                      </span>
                      <Tooltip 
                        id='reference_chunk' 
                        place="bottom" 
                        style={{ 
                          color: configData?.app_ui_settings?.ui_fontcolor,
                          backgroundColor: lighterBackground
                        }}
                      >
                        Reference
                      </Tooltip>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div ref={messagesEndRef} />

      {loading && (
        <div ref={loadingIndicatorRef} className="md:pl-[2%] lg:pl-[10%] flex items-center text-lg font-semibold text-sm">
          {safeAiIconSrc ? (
            <img
              src={safeAiIconSrc}
              alt="AI Face"
              className="h-6 w-6"
              style={{ color: configData.app_ui_settings.ui_iconcolor }}
            />
          ) : (
<FaRobot
              className="h-6 w-6"
              style={{ color: configData.app_ui_settings.ui_fontcolor }}
            />
          )}
          &nbsp; Generating<span className="dot-typing"></span>
        </div>
      )}
    </div>
  );
};

export default MessageList;